import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { namespace, CHROME_WEB_STORE_URL } from '@common-config/constants';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Chrome, Server, Sparkles, PlayCircle } from 'lucide-react';
import { HlsPlayer } from '@/common/components/hls-player/hls-player';
import { AnalyticsService } from '@/common/services/analytics-service';
import { navigateToEngines } from '@/common/utils/navigation.util';

// Onboarding explainer (GCS-hosted HLS, transcoded via GCP Transcoder — replaces Mux)
const ONBOARDING_VIDEO_HLS =
  'https://storage.googleapis.com/blueticks-public-videos/onboarding/connection-choice/en/manifest.m3u8';
const ONBOARDING_VIDEO_POSTER =
  'https://storage.googleapis.com/blueticks-public-videos/onboarding/connection-choice/poster.jpg';

interface WelcomePopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export const WelcomePopup: React.FC<WelcomePopupProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation(namespace);
  const analyticsService = new AnalyticsService();

  // Track popup view when it opens
  useEffect(() => {
    if (isOpen) {
      analyticsService.event('welcome_popup_view', {
        category: 'onboarding',
        action: 'view_welcome_popup',
        label: 'popup_shown',
        value: 1
      });
    }
  }, [isOpen, analyticsService]);

  const handleChromeExtensionClick = () => {
    // Track analytics
    analyticsService.event('welcome_chrome_extension_click', {
      category: 'onboarding',
      action: 'click_chrome_extension',
      label: 'welcome_popup',
      value: 1
    });
    analyticsService.event('onboarding_path_chosen', {
      category: 'onboarding',
      path: 'extension',
    });

    // Open Chrome Web Store in new tab
    window.open(CHROME_WEB_STORE_URL, '_blank');
  };

  const handleGatewayClick = () => {
    // Track analytics
    analyticsService.event('welcome_gateway_click', {
      category: 'onboarding',
      action: 'click_gateway_setup',
      label: 'welcome_popup',
      value: 1
    });
    analyticsService.event('onboarding_path_chosen', {
      category: 'onboarding',
      path: 'gateway',
    });

    // Navigate to engines management page
    onClose();
    navigateToEngines();
  };

  const handleSkip = () => {
    // Track analytics
    analyticsService.event('welcome_skip_click', {
      category: 'onboarding',
      action: 'skip_setup',
      label: 'welcome_popup',
      value: 1
    });
    analyticsService.event('onboarding_path_chosen', {
      category: 'onboarding',
      path: 'skip',
    });

    // Just close the popup and go to calendar
    onClose();
  };

  // Video interaction handlers
  const handleVideoPlay = () => {
    analyticsService.event('welcome_video_play', {
      category: 'onboarding',
      action: 'play_explainer_video',
      label: 'welcome_popup',
      value: 1
    });
  };

  const handleVideoReady = () => {
    analyticsService.event('welcome_video_ready', {
      category: 'onboarding',
      action: 'video_loaded',
      label: 'welcome_popup',
      value: 1
    });
  };

  const buttonHoverStyles = {
    onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget.style.background = 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #3b82f6 100%)';
      e.currentTarget.style.boxShadow = '0 8px 25px rgba(37, 99, 235, 0.4)';
    },
    onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)';
      e.currentTarget.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
    },
    onMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget.style.background = 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 50%, #2563eb 100%)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.5)';
    },
    onMouseUp: (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget.style.background = 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #3b82f6 100%)';
      e.currentTarget.style.boxShadow = '0 8px 25px rgba(37, 99, 235, 0.4)';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="overflow-y-auto p-0 rounded-none md:rounded-lg"
        style={{
          position: 'fixed',
          inset: 0,
          transform: 'none',
          translate: 'none',
          width: '100%',
          height: '100%',
          maxWidth: 'none',
          maxHeight: 'none',
        }}>
        <div className="p-4 sm:p-6">
          {/* Header */}
          <DialogHeader className="text-center mb-2 sm:mb-4">
            <div className="flex justify-center mb-3">
              <div className="p-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-600">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
            </div>
            <DialogTitle className="text-xl font-bold mb-2 text-center" style={{ color: 'var(--color-text)' }}>
              {t('welcome.title', 'Welcome to Blueticks!')}
            </DialogTitle>
            <DialogDescription className="text-base text-center" style={{ color: 'var(--color-text-muted)' }}>
              {t('welcome.subtitle', 'Get started by connecting to WhatsApp to send your first scheduled message.')}
            </DialogDescription>
          </DialogHeader>

          {/* Connection Options */}
          <div className="space-y-4 mb-4 sm:mb-6 w-full md:w-[1000px] max-w-[95vw] mx-auto">
            <h3 className="text-base font-semibold mb-2 sm:mb-4 text-center" style={{ color: 'var(--color-text)' }}>
              {t('welcome.chooseConnection', 'Choose your connection method:')}
            </h3>
            
            <div className="flex flex-col md:grid md:grid-cols-3 gap-2 sm:gap-4 w-full items-center">
              {/* Mobile: Video first, then options */}
              <div className="md:hidden order-1 w-full flex justify-center">
                <div className="flex flex-col items-center justify-center space-y-2 px-2">
                  <div className="w-full max-w-[140px] rounded-lg overflow-hidden shadow-lg bg-gray-100" style={{ aspectRatio: '9/16' }}>
                    <HlsPlayer
                      videoUrl={ONBOARDING_VIDEO_HLS}
                      poster={ONBOARDING_VIDEO_POSTER}
                      title="Connection Types Explained"
                      width="100%"
                      height="100%"
                      autoplay={true}
                      muted={false}
                      controls={true}
                      className="rounded-lg"
                      onPlay={handleVideoPlay}
                      onLoadedData={handleVideoReady}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-medium text-gray-600 mb-1">
                      {t('welcome.videoTitle', 'Watch: Connection Types Explained')}
                    </p>
                    <div className="flex items-center justify-center space-x-1 text-xs text-gray-500">
                      <PlayCircle className="h-3 w-3" />
                      <span>{t('welcome.videoDescription', 'See the difference')}</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Chrome Extension Option */}
              <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-blue-300 h-full w-full min-w-0 order-2 md:order-1">
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="flex flex-col items-center text-center space-y-2">
                    <div className="p-2 rounded-lg bg-orange-100">
                      <Chrome className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg mb-1">{t('welcome.chromeExtension', 'Chrome Extension')}</CardTitle>
                      <CardDescription className="text-xs">
                        {t('welcome.chromeExtensionDesc', 'Free • Works with WhatsApp Web • Requires browser to be open')}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 flex flex-col justify-between flex-1">
                  <p className="hidden sm:block text-xs text-muted-foreground mb-4 text-center">
                    {t('welcome.chromeExtensionDetails', 'Perfect for personal use. Install the extension and keep WhatsApp Web open to send messages.')}
                  </p>
                  <Button
                    onClick={handleChromeExtensionClick}
                    className="w-full relative overflow-hidden transition-all duration-300 ease-out hover:shadow-xl active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-blue-300/50"
                    style={{
                      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)',
                      color: '#ffffff',
                      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
                    }}
                    {...buttonHoverStyles}
                  >
                    <Chrome className="h-4 w-4 mr-2" />
                    {t('welcome.installExtension', 'Install Extension')}
                  </Button>
                </CardContent>
              </Card>

              {/* Explainer Video - Desktop only */}
              <div className="hidden md:flex flex-col items-center justify-center space-y-3 px-2 order-2">
                <div className="w-full max-w-[220px] rounded-lg overflow-hidden shadow-lg bg-gray-100" style={{ aspectRatio: '9/16' }}>
                  <HlsPlayer
                    videoUrl={ONBOARDING_VIDEO_HLS}
                    poster={ONBOARDING_VIDEO_POSTER}
                    title="Connection Types Explained"
                    width="100%"
                    height="100%"
                    autoplay={true}
                    muted={false}
                    controls={true}
                    className="rounded-lg"
                    onPlay={handleVideoPlay}
                    onLoadedData={handleVideoReady}
                  />
                </div>
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-600 mb-1">
                    {t('welcome.videoTitle', 'Watch: Connection Types Explained')}
                  </p>
                  <div className="flex items-center justify-center space-x-1 text-xs text-gray-500">
                    <PlayCircle className="h-3 w-3" />
                    <span>{t('welcome.videoDescription', 'See the difference')}</span>
                  </div>
                </div>
              </div>

              {/* Gateway Option */}
              <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-purple-300 h-full w-full min-w-0 order-3">
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="flex flex-col items-center text-center space-y-2">
                    <div className="p-2 rounded-lg bg-purple-100">
                      <Server className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg mb-1">{t('welcome.whatsappGateway', 'WhatsApp Gateway')}</CardTitle>
                      <CardDescription className="text-xs">
                        {t('welcome.whatsappGatewayDesc', 'Pro Plan • Always-on sending • No browser required')}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 flex flex-col justify-between flex-1">
                  <p className="hidden sm:block text-xs text-muted-foreground mb-4 text-center">
                    {t('welcome.whatsappGatewayDetails', 'Ideal for businesses. Send messages even when your computer is off.')}
                  </p>
                  <Button
                    onClick={handleGatewayClick}
                    variant="outline"
                    className="w-full hover:bg-purple-50 hover:border-purple-300 transition-colors"
                    style={{ borderColor: 'var(--color-border)', color: 'var(--color-text)' }}
                  >
                    <Server className="h-4 w-4 mr-2" />
                    {t('welcome.setupGateway', 'Setup Gateway')}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Skip Option */}
          <div className="text-center pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <Button
              onClick={handleSkip}
              variant="ghost"
              className="text-xs hover:bg-gray-50"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {t('welcome.skip', 'Skip for now, I\'ll set this up later')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
